import React, { useState } from 'react';
import SectionEdition, { Slot } from 'components/styleguide/section_edition/SectionEdition';
import Sheet from 'components/styleguide/sheet_modal/Sheet';
import InputField from 'components/styleguide/input_field/InputField';
import SelectField from 'components/styleguide/select_field/SelectField';
import Textarea from 'components/styleguide/textarea/Textarea';
import Checkbox from 'components/styleguide/checkbox/Checkbox';
import DatePicker from 'components/styleguide/date_picker/DatePicker';
import { useEditableSheet } from 'components/styleguide/sheet_modal/useEditableSheet';
import Button from 'components/styleguide/button/Button';
import Dialog from 'components/styleguide/dialog/Dialog';
import SearchInput from 'components/styleguide/search_input/SearchInput';
const styles = require('../onboarding-cards.module.scss');

type Option = { value: string | number; label: string };

interface Experience {
  id: number;
  position: string;
  company: string;
  contract_type: string;
  category_ids: number[];
  categories: Option[];
  description: string;
  start_month: number;
  start_year: number;
  end_month: number | null;
  end_year: number | null;
  current_position: boolean;
  start_date_decorated: string;
  end_date_decorated: string | null;
}

interface WorkExperienceSectionProps {
  experiences: Experience[];
  categories: Option[];
  jobTypes: Option[];
  createUrl: string;
  updateUrl: string;
}

const WorkExperienceSection: React.FC<WorkExperienceSectionProps> = ({
  experiences: initialExperiences,
  categories,
  jobTypes,
  createUrl,
  updateUrl,
}) => {
  const [experiences, setExperiences] = useState<Experience[]>(initialExperiences || []);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const sheet = useEditableSheet({
    url: createUrl,
    method: 'POST',
    payloadKey: 'professional_experience',
    onSuccess: (_formData, responseData) => {
      if (editingId != null) {
        setExperiences(prev => prev.map(e => e.id === editingId ? responseData.data : e));
      } else {
        setExperiences(prev => [...prev, responseData.data]);
      }
    },
    onDelete: (id: number) => {
      setExperiences(prev => prev.filter(e => e.id !== id));
    },
  });

  const status = experiences.length > 0 ? 'complete' : 'missing';

  const toTimestamp = (month: number | null, year: number | null): number | null => {
    if (month && year) return new Date(year, month - 1, 10).getTime() / 1000;
    return null;
  };

  const fromTimestamp = (ts: number | null): { month: number; year: number } | null => {
    if (!ts) return null;
    const d = new Date(ts * 1000);
    return { month: d.getMonth() + 1, year: d.getFullYear() };
  };

  const openAdd = () => {
    setEditingId(null);
    sheet.open('Add work experience', {
      position: '',
      company: '',
      contract_type: '',
      category_ids: [],
      description: '',
      start_date: null,
      end_date: null,
      current_position: false,
    }, { url: createUrl, method: 'POST' });
  };

  const openEdit = (exp: Experience) => {
    setEditingId(exp.id);
    sheet.open('Edit work experience', {
      position: exp.position,
      company: exp.company,
      contract_type: exp.contract_type,
      category_ids: exp.category_ids,
      description: exp.description || '',
      start_date: toTimestamp(exp.start_month, exp.start_year),
      end_date: toTimestamp(exp.end_month, exp.end_year),
      current_position: exp.current_position,
    }, { url: `${updateUrl}/${exp.id}`, method: 'PATCH' });
  };

  const handleDelete = (id: number) => {
    sheet.handleDelete(`${updateUrl}/${id}`, id);
  };

  const formatDateRange = (exp: Experience): string => {
    const start = exp.start_date_decorated;
    const end = exp.current_position ? 'Present' : exp.end_date_decorated;
    return [start, end].filter(Boolean).join(' - ');
  };

  const selectedCategories = categories.filter(c =>
    (sheet.formData.category_ids || []).includes(c.value)
  );

  return (
    <>
      <SectionEdition
        title="Work experience"
        iconName="graduationCap"
        context="talent"
        status={status}
        onAdd={openAdd}
        allowMultiple={true}
        defaultDeleteConfirmation={{
          title: 'Delete this experience?',
          message: "You'll remove it from your profile permanently.",
        }}
      >
        {experiences.map(exp => (
          <Slot
            key={exp.id}
            onEdit={() => openEdit(exp)}
            onDelete={() => handleDelete(exp.id)}
          >
            <div className={styles.fieldGridCompact}>
              <p className={styles.inputText}>{exp.position}</p>
              <p className={styles.label}>
                {exp.company} &bull; {formatDateRange(exp)}
              </p>
              {exp.description && (
                <p className={styles.truncatedValue}>
                  {exp.description}
                </p>
              )}
            </div>
          </Slot>
        ))}
      </SectionEdition>

      {sheet.isOpen && (
        <Sheet
          title={sheet.sheetTitle}
          onClose={sheet.close}
          context="talent"
          showCancelButton={false}
          continueButtonText="Save"
          onContinue={sheet.handleSave}
        >
          <div className={styles.form}>
            <InputField
              label="Position"
              placeholder="e.g. Full Stack Developer"
              name="position"
              required={true}
              value={sheet.formData.position || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="M"
              error={sheet.getError('position')}
              context="talent"
            />
            <SearchInput
              initialItems={selectedCategories}
              options={categories}
              label="Category"
              placeholder="e.g. UX Designer"
              required
              error={sheet.getError('category_ids')}
              onChange={(selected: any) => sheet.handleFormChange('category_ids', (selected || []).map((o: Option) => o.value))}
              context="talent"
            />
            <InputField
              label="Company"
              placeholder="e.g. Landing.jobs"
              name="company"
              required={true}
              value={sheet.formData.company || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              size="M"
              error={sheet.getError('company')}
              context="talent"
            />
            <SelectField
              label="Contract type"
              name="contract_type"
              placeholder="e.g. Full-time"
              options={jobTypes}
              value={jobTypes.find((o: Option) => o.value === sheet.formData.contract_type) || null}
              onChange={(v: any) => sheet.handleFormChange('contract_type', v?.value)}
              size="M"
              required={true}
              error={sheet.getError('contract_type')}
              context="talent"
            />
            <div className={styles.fieldRow}>
              <DatePicker
                label="Start date"
                placeholder="Select date"
                required={true}
                maxDate={fromTimestamp(sheet.formData.end_date) || 'today'}
                yearLimit={80}
                value={fromTimestamp(sheet.formData.start_date)}
                onChange={(val: any) => sheet.handleFormChange('start_date', val ? toTimestamp(val.month, val.year) : null)}
                error={sheet.getError('start_date')}
                context="talent"
              />
              <DatePicker
                label="End date"
                placeholder="Select date"
                required={!sheet.formData.current_position}
                disabled={!!sheet.formData.current_position}
                minDate={fromTimestamp(sheet.formData.start_date)}
                maxDate="today"
                yearLimit={80}
                value={fromTimestamp(sheet.formData.end_date)}
                onChange={(val: any) => sheet.handleFormChange('end_date', val ? toTimestamp(val.month, val.year) : null)}
                error={sheet.getError('end_date')}
                context="talent"
              />
            </div>
            <Checkbox
              id="current-position"
              name="current_position"
              label="I currently work in this position"
              checked={!!sheet.formData.current_position}
              onChange={(e) => {
                sheet.handleFormChange('current_position', e.target.checked);
                if (e.target.checked) sheet.handleFormChange('end_date', null);
              }}
              context="talent"
            />
            <Textarea
              label="Description"
              name="description"
              placeholder="Describe your responsibilities and achievements"
              defaultValue={sheet.formData.description || ''}
              onChange={(e) => sheet.handleFormChange(e.target.name, e.target.value)}
              rows={6}
              error={sheet.getError('description')}
              context="talent"
            />
            {editingId && (
              <Button
                type="secondary"
                size="M"
                iconLeft="trash"
                context="talent"
                onClick={() => setIsDeleteConfirmOpen(true)}
              >
                Delete experience
              </Button>
            )}
          </div>
        </Sheet>
      )}
      <Dialog
        isOpen={isDeleteConfirmOpen}
        title="Delete this experience?"
        message="You'll remove it from your profile permanently."
        confirmText="Delete"
        context="talent"
        onClose={() => setIsDeleteConfirmOpen(false)}
        onConfirm={() => {
          setIsDeleteConfirmOpen(false);
          if (editingId) handleDelete(editingId);
        }}
      />
    </>
  );
};

export default WorkExperienceSection;
